import React, { useCallback, useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { StatusBadge } from 'components/StatusBadge';
import { Input } from 'components/ui/input';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import api, { getApiErrorMessage } from 'lib/api';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import { Search, BadgeCheck, Ban, Eye, FileText, ExternalLink, Trash2, Download } from 'lucide-react';
import { formatDateTime } from 'lib/format';
import { useLanguage } from 'lib/i18n';

const DOC_TYPE_KEYS = {
  id_card: 'admin.documents.identityCard',
  license: 'admin.documents.license',
  insurance: 'admin.documents.insurance',
  vehicle_registration: 'admin.documents.vehicleRegistration',
};

const PAGE_SIZE = 20;

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
  const [error, setError] = useState('');
  const [busyUserId, setBusyUserId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [userDetails, setUserDetails] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filter === 'pending') {
        params.set('pending_identity', 'true');
      } else if (filter !== 'all') {
        params.set('role', filter);
      }
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));

      const r = await api.get(`/admin/users?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setUsers(items);
      setPagination(meta);
    } catch (e) {
      setUsers([]);
      setError(getApiErrorMessage(e, t('admin.users.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [filter, page, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter, q]);

  const action = async (user_id, act) => {
    let reason;
    if (act === 'reject_identity') {
      reason = window.prompt(t('admin.users.rejectPrompt'), '') || '';
    }
    if (act === 'delete') {
      const target = users.find((item) => item.id === user_id);
      const label = target?.full_name || target?.email || t('admin.users.otherUser');
      if (!window.confirm(`${t('admin.users.deleteConfirmPrefix')}${label}${t('admin.users.deleteConfirmSuffix')}`)) return;
    }
    if (act === 'make_admin') {
      const target = users.find((item) => item.id === user_id);
      const label = target?.full_name || target?.email || t('admin.users.otherUser');
      if (!window.confirm(`${t('admin.users.makeAdminConfirmPrefix')}${label}${t('admin.users.makeAdminConfirmSuffix')}`)) return;
    }
    setBusyUserId(user_id);
    try {
      await api.post(`/admin/users/${user_id}/action`, { action: act, reason });
      toast.success(t('admin.users.actionDone'));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.users.actionFailed')));
    } finally {
      setBusyUserId(null);
    }
  };

  const openDetails = async (userId) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setUserDetails(null);
    try {
      const r = await api.get(`/admin/users/${userId}`);
      setUserDetails(r.data);
    } catch (e) {
      setDetailsError(getApiErrorMessage(e, t('admin.users.profileLoadFailed')));
    } finally {
      setDetailsLoading(false);
    }
  };

  const detailUser = userDetails?.user || {};
  const detailStats = userDetails?.stats || {};
  const documents = detailUser?.deliverer_documents || [];

  const exportUsersCsv = async () => {
    try {
      const response = await api.get('/admin/users/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'colibuddy-users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.users.exportFailed')));
    }
  };

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('admin.users.title')}</h1>
        <Button variant="outline" onClick={exportUsersCsv}><Download size={14} className="mr-1" /> {t('admin.exportCsv')}</Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">{t('admin.users.all')}</TabsTrigger>
                <TabsTrigger value="sender">{t('admin.users.senders')}</TabsTrigger>
                <TabsTrigger value="traveler">{t('admin.users.travelers')}</TabsTrigger>
                <TabsTrigger value="deliverer">{t('admin.users.couriers')}</TabsTrigger>
                <TabsTrigger value="pending" data-testid="admin-tab-pending">{t('admin.users.pendingIdentity')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 w-64" placeholder={t('admin.users.searchPlaceholder')} value={q} onChange={e => setQ(e.target.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.users.name')}</TableHead>
                <TableHead>{t('admin.users.email')}</TableHead>
                <TableHead>{t('admin.users.roles')}</TableHead>
                <TableHead>{t('admin.users.status')}</TableHead>
                <TableHead>{t('admin.users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>
              )}
              {!loading && error && (
                <TableRow><TableCell colSpan={5} className="text-center text-destructive py-6">{error}</TableCell></TableRow>
              )}
              {!loading && !error && users.map(u => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {(u.roles || []).map(r => <Badge key={r} variant="outline">{({ sender: t('role.sender'), traveler: t('role.traveler'), deliverer: t('role.deliverer') })[r] || r}</Badge>)}
                      {u.is_admin && <Badge>{t('admin.users.admin')}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const hasPendingIdentity = !u.is_verified_id && (u.deliverer_documents || []).some((d) => (d?.status || 'pending') === 'pending');
                      if (u.suspended) return <Badge variant="destructive">{t('admin.users.suspended')}</Badge>;
                      if (hasPendingIdentity) return <Badge variant="outline">{t('admin.users.identityPending')}</Badge>;
                      if (u.deliverer_validated) return <Badge className="bg-emerald-100 text-emerald-800 border-0">{t('admin.users.validated')}</Badge>;
                      if (u.roles?.includes('deliverer')) return <Badge variant="outline">{t('admin.users.pending')}</Badge>;
                      return <Badge variant="secondary">{t('admin.users.active')}</Badge>;
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openDetails(u.id)}><Eye size={14} className="mr-1"/>{t('admin.users.details')}</Button>
                      {!u.is_verified_id && (u.deliverer_documents || []).some((d) => (d?.status || 'pending') === 'pending') && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyUserId === u.id}
                          onClick={() => action(u.id, 'validate_identity')}
                        >
                          <BadgeCheck size={14} className="mr-1"/>{t('admin.users.validateIdentity')}
                        </Button>
                      )}
                      {!u.is_verified_id && (u.deliverer_documents || []).some((d) => (d?.status || 'pending') === 'pending') && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyUserId === u.id}
                          onClick={() => action(u.id, 'reject_identity')}
                        >
                          {t('admin.users.rejectIdentity')}
                        </Button>
                      )}
                      {u.roles?.includes('deliverer') && !u.deliverer_validated && (
                        <Button size="sm" disabled={busyUserId === u.id} onClick={() => action(u.id, 'validate_deliverer')} data-testid={`validate-${u.id}-button`}><BadgeCheck size={14} className="mr-1"/>{t('admin.users.validate')}</Button>
                      )}
                      {!u.suspended ? (
                        <Button size="sm" variant="outline" disabled={busyUserId === u.id} onClick={() => action(u.id, 'suspend')} aria-label={t('admin.users.suspend')} title={t('admin.users.suspend')}><Ban size={14}/></Button>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busyUserId === u.id} onClick={() => action(u.id, 'unsuspend')}>{t('admin.users.reactivate')}</Button>
                      )}
                      {!u.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyUserId === u.id}
                          onClick={() => action(u.id, 'make_admin')}
                          title={t('admin.users.grantAdmin')}
                          data-testid={`make-admin-${u.id}-button`}
                        >
                          <BadgeCheck size={14} className="mr-1" /> {t('admin.users.admin')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busyUserId === u.id || currentUser?.id === u.id}
                        onClick={() => action(u.id, 'delete')}
                        title={currentUser?.id === u.id ? t('admin.users.cannotDeleteSelf') : t('admin.users.deletePermanently')}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && users.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.users.none')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {pagination.total} {t('admin.usersCount')} - {t('admin.pagination.page')} {pagination.page}/{pagination.total_pages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={loading || pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('admin.previous')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={loading || pagination.page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
              >
                {t('admin.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.users.detailsTitle')}</DialogTitle>
          </DialogHeader>

          {detailsLoading && <p className="text-sm text-muted-foreground">{t('admin.loading')}</p>}
          {!detailsLoading && detailsError && <p className="text-sm text-destructive">{detailsError}</p>}

          {!detailsLoading && !detailsError && userDetails && (
            <div className="space-y-5">
              <section>
                <h3 className="font-semibold mb-2">{t('admin.users.information')}</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">{t('admin.users.name')}:</span> {detailUser.full_name || '-'}</div>
                  <div><span className="text-muted-foreground">{t('admin.users.email')}:</span> {detailUser.email || '-'}</div>
                  <div><span className="text-muted-foreground">{t('admin.users.phone')}:</span> {detailUser.phone || '-'}</div>
                  <div><span className="text-muted-foreground">{t('admin.users.city')}:</span> {detailUser.city || '-'}</div>
                  <div><span className="text-muted-foreground">{t('admin.users.country')}:</span> {detailUser.country || '-'}</div>
                  <div><span className="text-muted-foreground">{t('admin.users.registeredAt')}</span> {formatDateTime(detailUser.created_at)}</div>
                </div>
                <div className="mt-3">
                  <p className="text-muted-foreground text-xs mb-1">{t('admin.users.roles')}</p>
                  <div className="flex gap-1 flex-wrap">
                    {(detailUser.roles || []).map((r) => <Badge key={r} variant="outline">{({ sender: t('role.sender'), traveler: t('role.traveler'), deliverer: t('role.deliverer') })[r] || r}</Badge>)}
                    {detailUser.is_admin && <Badge>{t('admin.users.admin')}</Badge>}
                    {detailUser.suspended && <Badge variant="destructive">{t('admin.users.suspended')}</Badge>}
                    {detailUser.deliverer_validated && <Badge className="bg-emerald-100 text-emerald-800 border-0">{t('admin.users.verifiedCourier')}</Badge>}
                  </div>
                </div>
              </section>

              <section>
                <h3 className="font-semibold mb-2">{t('admin.users.verifications')}</h3>
                <div className="grid sm:grid-cols-3 gap-2 text-sm">
                  <div className="p-2 border rounded-md">{t('admin.users.email')}: {detailUser.is_verified_email ? t('admin.users.yes') : t('admin.users.no')}</div>
                  <div className="p-2 border rounded-md">{t('admin.users.phone')}: {detailUser.is_verified_phone ? t('admin.users.yes') : t('admin.users.no')}</div>
                  <div className="p-2 border rounded-md">{t('admin.users.identity')}: {detailUser.is_verified_id ? t('admin.users.yes') : t('admin.users.no')}</div>
                </div>
              </section>

              <section>
                <h3 className="font-semibold mb-2">{t('admin.users.activity')}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                  <div className="p-3 border rounded-md"><p className="text-xs text-muted-foreground">{t('admin.users.parcelsPublished')}</p><p className="text-xl font-bold">{detailStats.deliveries_created || 0}</p></div>
                  <div className="p-3 border rounded-md"><p className="text-xs text-muted-foreground">{t('admin.users.tripsPublished')}</p><p className="text-xl font-bold">{detailStats.trips_created || 0}</p></div>
                  <div className="p-3 border rounded-md"><p className="text-xs text-muted-foreground">{t('admin.users.relatedMissions')}</p><p className="text-xl font-bold">{detailStats.missions_involved || 0}</p></div>
                  <div className="p-3 border rounded-md"><p className="text-xs text-muted-foreground">{t('admin.users.openDisputes')}</p><p className="text-xl font-bold">{detailStats.open_disputes || 0}</p></div>
                </div>
              </section>

              <section>
                <h3 className="font-semibold mb-2">{t('admin.users.identityDocuments')}</h3>
                {documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('admin.users.noDocuments')}</p>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc, i) => (
                      <div key={`${doc.document_type}-${i}`} className="border rounded-md p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <FileText size={14} />
                              <p className="font-medium text-sm">{DOC_TYPE_KEYS[doc.document_type] ? t(DOC_TYPE_KEYS[doc.document_type]) : (doc.document_type || t('admin.documents.document'))}</p>
                              <StatusBadge status={doc.status || 'pending'} />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{t('admin.users.submitted')} {formatDateTime(doc.submitted_at)}</p>
                            {doc.admin_comment && <p className="text-xs mt-1 text-destructive">{t('admin.users.adminComment')} {doc.admin_comment}</p>}
                            {doc.notes && <p className="text-xs mt-1">{t('admin.users.note')} {doc.notes}</p>}
                          </div>
                          {doc.document_url && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={doc.document_url} target="_blank" rel="noreferrer">
                                <ExternalLink size={14} className="mr-1" />{t('admin.users.openDocument')}
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
